import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * MultiSelectDropdown (Bootstrap 5 + React, no external JS)
 * - Checkbox list dentro de un dropdown
 * - Buscar en la lista, Seleccionar todo, Limpiar, y badges de lo seleccionado
 * - Controlled (value/onChange) or Uncontrolled (defaultValue)
 *
 * Props
 *  - options: Array<string | { value: string; label?: string; disabled?: boolean }>
 *  - value?: string[]
 *  - defaultValue?: string[]
 *  - onChange?: (values: string[]) => void
 *  - placeholder?: string (default: "Selecciona opciones")
 *  - buttonClassName?: string (classes extra para el boton trigger)
 *  - maxBadges?: number (cuantos badges mostrar antes de "+N")
 */
export default function MultiSelectDropdown({
  options = [],
  value,
  defaultValue = [],
  onChange,
  onBlur,
  placeholder = "Selecciona opciones",
  buttonClassName = "",
  maxBadges = 2,
}) {
  const [open, setOpen] = useState(false);
  const isControlled = value !== undefined;
  const [internal, setInternal] = useState(defaultValue);
  const selected = isControlled ? value : internal;
  const [q, setQ] = useState("");
  const wrapperRef = useRef(null);

  const normalized = useMemo(
    () =>
      options.map((o) =>
        typeof o === "string" ? { value: o, label: o, disabled: false } : { label: o.label ?? String(o.value), value: String(o.value), disabled: !!o.disabled }
      ),
    [options]
  );

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return normalized;
    return normalized.filter((o) =>
      o.label.toLowerCase().includes(term) || o.value.toLowerCase().includes(term)
    );
  }, [q, normalized]);

  const allEnabledValues = useMemo(
    () => normalized.filter((o) => !o.disabled).map((o) => o.value),
    [normalized]
  );

  const setSelected = (next) => {
    if (isControlled) {
      onChange?.(next);
    } else {
      setInternal(next);
      onChange?.(next);
    }
  };

  const toggle = () => setOpen((s) => !s);

  // Se cierra si se cliquea afuera
  useEffect(() => {
    function onDocClick(e) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Teclado: 'Esc' lo cierra
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") {
        setOpen(false);
        onBlur?.();
      }
    }
    if (open) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onBlur]);

  const isChecked = (v) => selected?.includes(v);

  const handleItemToggle = (v) => {
    const set = new Set(selected);
    if (set.has(v)) set.delete(v);
    else set.add(v);
    setSelected(Array.from(set));
  };

  const handleSelectAll = () => setSelected(allEnabledValues);
  const handleClear = () => setSelected([]);

  // Badge
  const selectedObjects = normalized.filter((o) => selected?.includes(o.value));
  const badgeLabels = selectedObjects.map((o) => o.label);
  const visibleBadges = badgeLabels.slice(0, maxBadges);
  const remaining = Math.max(0, badgeLabels.length - visibleBadges.length);

  const btnText = badgeLabels.length
    ? (
        <span className="d-inline-flex flex-wrap gap-1 align-items-center">
          {visibleBadges.map((l) => (
            <span key={l} className="badge text-bg-primary text-wrap">{l}</span>
          ))}
          {remaining > 0 && (
            <span className="badge text-bg-primary">+{remaining}</span>
          )}
        </span>
      )
    : (
        <span className="text-body-secondary">{placeholder}</span>
      );

  return (
    <div ref={wrapperRef} className="dropdown" style={{ position: "relative" }}>
      <button
        type="button"
        className={`form-select ${buttonClassName}`}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={toggle}
        onBlur={onBlur}
      >
        {btnText}
      </button>

      {open && (
        <div
          className="dropdown-menu show p-2 shadow"
          style={{ minWidth: 280, maxHeight: 320, overflow: "auto" }}
          role="listbox"
        >
          <div className="mb-2 d-flex gap-2">
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Buscar…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Buscar opciones"
            />
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={handleClear}>
              Limpiar
            </button>
          </div>

          <div className="d-flex gap-2 mb-2">
            <button type="button" className="btn btn-sm btn-light border" onClick={handleSelectAll}>
              Seleccionar todo
            </button>
            <button type="button" className="btn btn-sm btn-light border" onClick={handleClear}>
              Quitar todo
            </button>
          </div>

          <ul className="list-unstyled mb-0" style={{ maxHeight: 220, overflowY: "auto"}}>
            {filtered.length === 0 && (
              <li className="text-center text-body-secondary py-2">Sin resultados</li>
            )}
            {filtered.map((o) => (
              <li key={o.value} className="form-check px-4 py-1">
                <input
                  id={`msd-${o.value}`}
                  className="form-check-input pl-"
                  type="checkbox"
                  disabled={o.disabled}
                  checked={!!isChecked(o.value)}
                  onChange={() => handleItemToggle(o.value)}
                />
                <label htmlFor={`msd-${o.value}`} className={`form-check-label ms-2 ${o.disabled ? "text-body-secondary" : ""}`}>
                  {o.label}
                </label>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

